/**
 * The words on the borrower's loan screen.
 *
 * A folder of its own rather than more of `estate.constants.ts`, because that
 * file holds the LENDER's side — the form that proposes a loan and the rail that
 * lists them — and this is the other party reading the same rows. The two sides
 * of a loan say different things about it, and `LOANS_COPY.loanStates` is the
 * proof: "Out" is a plain fact to the owner and "Here, with you" to the museum
 * holding the crate.
 *
 * PLAIN LANGUAGE AND NO JARGON. Nothing here names a status value, a route or a
 * function. A registrar at a museum is the reader.
 */

import type { LoanStatus } from "@ondo/shared";

export const BORROWED_COPY = {
  loading: "Loading loans offered to you…",
  errorTitle: "These loans could not be loaded",

  empty: "Nobody has offered you a loan.",
  emptyDetail:
    "When a collector or an estate offers to lend you a work, it will appear here for you to accept or decline.",

  count: (n: number) => (n === 1 ? "1 loan" : `${n} loans`),

  /**
   * What each state means TO THE BORROWER, which is not what it means to the
   * lender. The owner's rail says "Out"; the museum holding the object is the
   * place where that word is least informative.
   */
  loanStates: {
    proposed: "Awaiting your answer",
    agreed: "Agreed — not yet sent",
    declined: "You declined this",
    out: "On its way to you",
    returned: "Returned",
    cancelled: "Withdrawn by the lender",
  } as Record<LoanStatus, string>,

  /** The four acts. Labels, and the label while the request is in flight. */
  accept: "Accept loan",
  accepting: "Accepting…",
  decline: "Decline",
  declining: "Declining…",
  received: "Mark as received",
  receiving: "Marking as received…",
  returned: "Mark as returned",
  returning: "Marking as returned…",

  /**
   * Said before accepting, because accepting is the moment a museum takes on
   * custody of somebody else's property and the screen should say what it does
   * and does not mean.
   */
  acceptNote:
    "Accepting means you agree to take the work. It does not transfer ownership, and it does not confirm anything about the work's history.",
  declineNote: "Declining ends this offer. The lender is told, and there is no undo.",

  /**
   * The receipt, and why it is a separate act from the loan being sent.
   *
   * A lender pressing "send" says they despatched it; only the borrower can say
   * it arrived. Nothing marks it from a date, and the sentence says so, because
   * a registrar who assumes the system will tick it over on the start date is
   * the person this feature fails.
   */
  receiveNote:
    "Only you can record that the work has arrived. Nothing marks it for you, on any date.",
  receivedOn: (day: string) => `You recorded this as received on ${day}.`,
  /**
   * WHAT THE RECEIPT PRODUCED, and the one piece of timing a registrar needs.
   *
   * Recording an act writes a dated line on the work's own record -- that is
   * what makes the museum's side something a reader can find, rather than
   * something inferred from the lender's silence. Asking the lender to put
   * their name to it is a separate act and always the recorder's choice:
   * recording is not requesting.
   *
   * The window is the part worth saying out loud. Marking the work returned
   * ends the loan and ends the museum's access to the record in the same
   * statement, so the ask has to happen first.
   */
  onTheRecord:
    "This is on the work's record now. You can ask the lender to confirm it from the work's history -- do that before you mark the work returned, because ending the loan ends your access.",
  returnNote:
    "Marking it returned ends the loan and ends your access to the work's record, including the lines you wrote on it.",

  /** States with nothing to press, each with the reason there is nothing. */
  waitingToBeSent: "The lender has not sent the work yet. There is nothing to do here until they do.",
  finished: "This loan is finished.",

  /** The four field labels on a row. */
  facts: {
    lender: 'Lender',
    venue: 'Going to',
    dates: 'Dates',
    purpose: 'Reason',
  },

  /** The counterparty, and the honest sentence when it cannot be resolved. */
  lenderUnknown: "Lender not shown",
  from: "from",
  until: "until",
  noDates: "No dates given",
  noPurpose: "No reason given",

  errors: {
    /**
     * A 403 or 404 on any of the four acts is almost always the same thing: the
     * row moved while the screen was open. Every door requires a specific
     * status, so a lender withdrawing — or a colleague answering in another tab
     * — turns the loan into something that no longer exists to answer.
     */
    gone: "This loan has changed since the screen was loaded. Reload to see where it stands.",
    refused: "That could not be recorded.",
  },
} as const;
